import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import { apiUrl } from "../../config/api";
import { parseTwoColumnExcel, matchExcelItems } from "../../utils/excelImport";

/* Настройка маршрута карточки ТМЦ — при необходимости поменяйте путь */
const makeTmcUrl = (id) => `${window.location.origin}/tmc/${id}`;

/* Общие утилиты */
const fmtMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0,00";
  return n.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtPercent = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0,00";
  return n.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtQty = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("ru-RU", { minimumFractionDigits: 0, maximumFractionDigits: 3 });
};
const parseNum = (s) => {
  const n = parseFloat(String(s ?? "").replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};
const formatSalePriceInput = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  if (num === 0) return "0";
  return num.toFixed(2).replace(".", ",");
};
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const EMPTY_TMC_ROW = {
  tmc_id: null,
  name: "",
  code: "",
  unit: "",
  price: 0,
  qty: 0,
  total: 0,
  supplier_link: "",
  for_sale: false,
  sale_price: 0,
  sale_sum: 0,
  sale_qty: 0,
  margin_percent: 0,
  profit: 0,
};
const createEmptyRow = () => ({ ...EMPTY_TMC_ROW });
const hasDraftContent = (row = {}) => {
  if (!row) return false;
  if (row.name && row.name.trim().length > 0) return true;
  if (row.code && String(row.code).trim().length > 0) return true;
  if (Number(row.qty) > 0) return true;
  if (Number(row.price) > 0) return true;
  if (Number(row.sale_price) > 0) return true;
  if (row.for_sale) return true;
  if (row.supplier_link && row.supplier_link.trim().length > 0) return true;
  return false;
};

const buildDeliveryKey = (item = {}) => {
  if (item.tmc_id != null && item.tmc_id !== "") return `id:${item.tmc_id}`;
  const name = String(item.name || "").trim().toLowerCase();
  if (!name) return "";
  return `name:${name}`;
};

export default function TmcSheet({ data, delivered = [], onChange, readOnlyLocked = false }) {
  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const [draftRow, setDraftRow] = useState(createEmptyRow());
  const rowsWithDraft = useMemo(() => [...rows, { ...draftRow, _isDraft: true }], [rows, draftRow]);

  const deliveredList = useMemo(() => (Array.isArray(delivered) ? delivered : []), [delivered]);
  const deliveredIndex = useMemo(() => {
    const map = new Map();
    deliveredList.forEach((item) => {
      const key = buildDeliveryKey(item);
      if (!key) return;
      const prev = map.get(key) || { qty: 0, sum: 0 };
      prev.qty += Number(item.delivered_qty) || 0;
      prev.sum += Number(item.delivered_sum) || 0;
      map.set(key, prev);
    });
    return map;
  }, [deliveredList]);
  const deliveredTotals = useMemo(
    () =>
      deliveredList.reduce(
        (acc, item) => ({
          qty: acc.qty + (Number(item.delivered_qty) || 0),
          sum: acc.sum + (Number(item.delivered_sum) || 0),
        }),
        { qty: 0, sum: 0 }
      ),
    [deliveredList]
  );

  const [tmcDict, setTmcDict] = useState([]);
  const [groups, setGroups] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const loadDicts = () => {
    fetch(apiUrl('/api/tmc'))
      .then((r) => r.json())
      .then((arr) => (Array.isArray(arr) ? setTmcDict(arr) : setTmcDict([])))
      .catch(() => setTmcDict([]));

    fetch(apiUrl('/api/tmc/groups'))
      .then((r) => r.json())
      .then((arr) => (Array.isArray(arr) ? setGroups(arr) : setGroups([])))
      .catch(() => setGroups([]));
  };

  useEffect(() => {
    loadDicts();
    const onFocus = () => loadDicts();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const groupNameById = useMemo(() => {
    const map = new Map();
    for (const g of groups) map.set(String(g.id), String(g.name || ""));
    return map;
  }, [groups]);

  const isConsumable = useCallback((item) => {
    const byName = String(item?.groupName || item?.group || "").toLowerCase().includes("расходн");
    if (byName) return true;
    const gid = item?.group_id ?? item?.groupId ?? item?.groupID;
    if (gid !== undefined && gid !== null) {
      const name = String(groupNameById.get(String(gid)) || "").toLowerCase();
      if (name.includes("расходн")) return true;
    }
    return false;
  }, [groupNameById]);
  const consumablesDict = useMemo(() => tmcDict.filter(isConsumable), [tmcDict, isConsumable]);

  const recalcRow = (row) => {
    const qty = Number(row.qty) || 0;
    const price = Number(row.price) || 0;
    const forSale = !!row.for_sale;
    const salePrice = Number(row.sale_price) || 0;

    const total = round2(qty * price);

    const saleQty = forSale ? qty : 0;
    const saleSum = round2(saleQty * salePrice);
    const costForSale = round2(saleQty * price);
    const profit = round2(saleSum - costForSale);
    const margin = costForSale > 0 ? round2((profit / costForSale) * 100) : 0;

    return { ...row, total, sale_qty: saleQty, sale_sum: saleSum, profit, margin_percent: margin };
  };

  const applyRow = (idx, mutator, opts) => {
    if (readOnlyLocked) return;
    if (idx >= rows.length) {
      setDraftRow((prev) => recalcRow(mutator({ ...prev })));
      return;
    }
    const next = rows.map((r, i) => (i === idx ? recalcRow(mutator({ ...r })) : r));
    onChange(next, opts);
  };
  const applyAll = (mutator, opts) => {
    if (readOnlyLocked) return;
    const next = rows.map((r) => recalcRow(mutator({ ...r })));
    onChange(next, opts);
  };

  const commitDraftRow = useCallback(
    (opts) => {
      if (readOnlyLocked) return;
      if (!hasDraftContent(draftRow)) return;
      const nextRow = recalcRow({ ...draftRow });
      onChange([...rows, nextRow], opts);
      setDraftRow(createEmptyRow());
    },
    [draftRow, onChange, readOnlyLocked, rows]
  );

  const removeRow = (idx) => {
    if (readOnlyLocked) return;
    onChange(rows.filter((_, i) => i !== idx), { forceCommit: true });
  };
  const handleDraftRowBlur = (event) => {
    if (readOnlyLocked) return;
    const stillInside = event?.currentTarget?.contains(event?.relatedTarget);
    if (stillInside) return;
    commitDraftRow({ forceCommit: true });
  };

  // Выбор позиции — цена фиксируется в карточке
  const onPickTmc = (idx, item) => {
    if (!item) {
      applyRow(idx, (row) => ({
        ...row,
        tmc_id: null,
        name: "",
        code: "",
        unit: "",
        price: 0,
        supplier_link: "",
        sale_price: row.for_sale ? 0 : row.sale_price,
        _salePriceInput: undefined,
      }));
      return;
    }
    applyRow(idx, (row) => {
      const nextPrice = Number(item.price) || 0;
      const shouldAutoSale = !!row.for_sale && !row._saleManual;
      const autoSalePrice = shouldAutoSale ? round2(nextPrice * 1.25) : row.sale_price;
      return {
        ...row,
        tmc_id: item.id,
        name: item.name ?? "",
        code: item.code ?? "",
        unit: item.unit ?? "",
        price: nextPrice,
        supplier_link: item.supplier_link ?? "",
        sale_price: autoSalePrice,
        _salePriceInput: undefined,
      };
    });
  };

  const toggleForSale = (idx, checked) => {
    applyRow(idx, (row) => {
      const nextSalePrice = checked ? round2((Number(row.price) || 0) * 1.25) : 0;
      return { ...row, for_sale: checked, sale_price: nextSalePrice, _saleManual: false, _salePriceInput: undefined };
    });
  };
  const changeSalePrice = (idx, rawValue, options = {}) => {
    if (readOnlyLocked) return;
    const { commit = false } = options;
    const raw = String(rawValue ?? "");
    const normalized = raw.replace(/\s/g, "").replace(/,/g, ".");
    const parsed = normalized === "" ? NaN : Number(normalized);
    const hasNumber = Number.isFinite(parsed);
    applyRow(
      idx,
      (row) => {
        const cleared = raw.trim() === "";
        const nextValue = hasNumber ? round2(parsed) : cleared ? 0 : row.sale_price;
        return {
          ...row,
          sale_price: nextValue,
          _saleManual: true,
          _salePriceInput: commit ? undefined : raw,
        };
      },
      commit ? { forceCommit: true } : undefined
    );
  };

  // Обновление цен по справочнику
  const refreshPrices = () => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    const byId = new Map();
    const byCode = new Map();
    for (const d of tmcDict) {
      if (d?.id !== undefined) byId.set(String(d.id), d);
      if (d?.code !== undefined) byCode.set(String(d.code), d);
    }
    applyAll((row) => {
      let dictItem = null;
      if (row.tmc_id != null) dictItem = byId.get(String(row.tmc_id));
      if (!dictItem && row.code != null) dictItem = byCode.get(String(row.code));
      if (!dictItem) return row;
      const newPrice = Number(dictItem.price) || 0;
      const newSalePrice = row.for_sale && !row._saleManual ? round2(newPrice * 1.25) : row.sale_price;
      return { ...row, price: newPrice, sale_price: newSalePrice };
    }, { forceCommit: true });
  };

  // Итоги
  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => {
          const qtyForSale = r.for_sale ? (Number(r.qty) || 0) : 0;
          const price = Number(r.price) || 0;
          acc.totalG += Number(r.total) || 0;
          acc.totalK += Number(r.sale_sum) || 0;
          acc.totalM += Number(r.profit) || 0;
          acc.totalCost += qtyForSale * price;
          return acc;
        },
        { totalG: 0, totalK: 0, totalM: 0, totalCost: 0 }
      ),
    [rows]
  );
  const weightedMargin = totals.totalCost > 0 ? round2((totals.totalM / totals.totalCost) * 100) : 0;

  // Стили
  const tableStyle = {
    borderCollapse: "separate",
    borderSpacing: 0,
    width: "100%",
    tableLayout: "auto",
    fontSize: "0.92rem",
  };
  const cellBase = {
    border: "1px solid #dfe3eb",
    padding: "4px 6px",
    fontSize: "0.93rem",
    background: "transparent",
    verticalAlign: "middle",
    overflow: "visible",
    whiteSpace: "normal",
    lineHeight: 1.25,
  };
  const thGray = { ...cellBase, background: "#f5f5f7", fontWeight: "bold", textAlign: "center" };
  const roGray = { ...cellBase, textAlign: "left" };
  const roGrayCenter = { ...cellBase, textAlign: "center" };
  const roGrayRight = { ...cellBase, textAlign: "right", paddingRight: 10 };
  const editWhite = { ...cellBase };
  const editWrap = { ...editWhite };
  const editWhiteCenter = { ...cellBase, textAlign: "center" };
  const editWhiteRight = { ...cellBase, textAlign: "right" };

  const thHeaderStack = { display: "flex", flexDirection: "column", alignItems: "stretch", justifyContent: "center", gap: 4 };
  const headerBtn = {
    display: "inline-block",
    marginTop: 4,
    padding: "4px 6px",
    fontSize: 12,
    border: "1px solid #c59bff",
    background: "#f5efff",
    color: "#5427b0",
    borderRadius: 6,
    cursor: "pointer",
    maxWidth: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    alignSelf: "stretch",
  };
  const inputNum = {
    width: "100%",
    padding: "6px 4px 4px",
    border: "none",
    borderBottom: "1px solid rgba(84,39,176,0.28)",
    background: "transparent",
    textAlign: "right",
    boxSizing: "border-box",
    fontSize: "0.95rem",
    fontVariantNumeric: "tabular-nums",
    outline: "none",
    color: "#111317",
  };

  const editLink = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    minWidth: 28,
    height: 28,
    border: "1px solid #c59bff",
    borderRadius: 6,
    color: "#5427b0",
    background: "#f5efff",
    cursor: "pointer",
    textDecoration: "none",
    fontWeight: 700,
  };

  const matchedDeliveredKeys = new Set();
  const renderedRows = rowsWithDraft.map((row, idx) => {
    const isDraft = idx === rows.length;
    const rowKey = isDraft ? "tmc-draft" : `tmc-${idx}`;
    const deliveryKey = !isDraft ? buildDeliveryKey(row) : "";
    const fact = !isDraft && deliveryKey ? deliveredIndex.get(deliveryKey) : null;
    if (fact && deliveryKey) matchedDeliveredKeys.add(deliveryKey);
    const factQty = fact ? fact.qty : null;
    const factSum = fact ? fact.sum : null;
    const draftEmpty = isDraft && !hasDraftContent(draftRow);

    return (
      <tr key={rowKey} onBlurCapture={isDraft ? handleDraftRowBlur : undefined}>
        <td style={isDraft ? { ...roGrayCenter, color: "#a3aab7" } : roGrayCenter}>{idx + 1}</td>

        <td style={editWrap}>
          <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
            <div style={{ flex: 1 }}>
              <NamePicker
                value={row.name || ""}
                dict={consumablesDict}
                onPick={(item) => onPickTmc(idx, item)}
                onClear={() => onPickTmc(idx, null)}
                disabled={readOnlyLocked}
              />
            </div>
            {row.tmc_id ? (
              <a
                href={makeTmcUrl(row.tmc_id)}
                target="_blank"
                rel="noopener noreferrer"
                title="Открыть карточку ТМЦ в новой вкладке"
                style={editLink}
              >
                ↗
              </a>
            ) : null}
          </div>
        </td>

        <td style={isDraft ? { ...roGrayCenter, color: "#a3aab7" } : roGrayCenter}>{row.code || ""}</td>

        <td style={editWhiteRight}>
          <input
            type="text"
            value={row.qty ?? ""}
            disabled={readOnlyLocked}
            inputMode="decimal"
            pattern="[0-9., ]*"
            onChange={(e) => applyRow(idx, (r) => ({ ...r, qty: parseNum(e.target.value) }))}
            style={inputNum}
          />
        </td>

        <td style={isDraft ? { ...roGrayCenter, color: "#a3aab7" } : roGrayCenter}>{row.unit || ""}</td>

        <td style={isDraft ? { ...roGrayRight, color: "#a3aab7" } : roGrayRight}>{draftEmpty ? "—" : fmtMoney(row.price)}</td>
        <td style={isDraft ? { ...roGrayRight, color: "#a3aab7" } : roGrayRight}>{draftEmpty ? "—" : fmtMoney(row.total)}</td>

        <td style={isDraft ? { ...roGrayRight, color: "#a3aab7" } : roGrayRight}>{factQty != null ? fmtQty(factQty) : "—"}</td>
        <td style={isDraft ? { ...roGrayRight, color: "#a3aab7" } : roGrayRight}>{factSum != null ? fmtMoney(factSum) : "—"}</td>

        <td style={{ ...roGray, whiteSpace: "normal", wordBreak: "break-all", ...(isDraft ? { color: "#a3aab7" } : null) }}>
          {row.supplier_link ? (
            <a href={row.supplier_link} target="_blank" rel="noopener noreferrer" style={{ color: "#3a27c5", textDecoration: "underline" }} title={row.supplier_link}>
              Ссылка
            </a>
          ) : (
            "—"
          )}
        </td>

        <td style={editWhiteCenter}>
          <input type="checkbox" checked={!!row.for_sale} disabled={readOnlyLocked} onChange={(e) => toggleForSale(idx, e.target.checked)} />
        </td>

        <td style={editWhiteRight}>
          <input
            type="text"
            value={row._salePriceInput ?? formatSalePriceInput(row.sale_price)}
            disabled={readOnlyLocked}
            inputMode="decimal"
            pattern="[0-9., ]*"
            onChange={(e) => changeSalePrice(idx, e.target.value)}
            onBlur={(e) => changeSalePrice(idx, e.target.value, { commit: true })}
            style={inputNum}
          />
        </td>

        <td style={isDraft ? { ...roGrayRight, color: "#a3aab7" } : roGrayRight}>{draftEmpty ? "—" : fmtMoney(row.sale_sum)}</td>
        <td style={isDraft ? { ...roGrayRight, color: "#a3aab7" } : roGrayRight}>{draftEmpty ? "—" : fmtPercent(row.margin_percent)}</td>

        <td style={roGrayRight}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={isDraft ? { color: "#a3aab7" } : undefined}>{draftEmpty ? "—" : fmtMoney(row.profit)}</span>
            {!isDraft && (
              <button
                type="button"
                onClick={() => removeRow(idx)}
                title="Удалить строку"
                disabled={readOnlyLocked}
                style={{ border: "none", background: "none", color: readOnlyLocked ? "#bbb" : "#c00", fontSize: 18, cursor: readOnlyLocked ? "not-allowed" : "pointer" }}
              >
                🗑️
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  });

  const unmatchedDelivered = deliveredList.filter((item) => {
    const key = buildDeliveryKey(item);
    if (!key) return false;
    return !matchedDeliveredKeys.has(key);
  });

  // Подтягиваем количество по позициям, которые уже есть в расчёте
  useEffect(() => {
    if (readOnlyLocked || !deliveredList.length || !rows.length) return;

    const rowIndex = new Map();
    rows.forEach((row, idx) => {
      const key = buildDeliveryKey(row);
      if (key) rowIndex.set(key, idx);
    });

    let changed = false;
    const nextRows = rows.map((row) => row);

    deliveredList.forEach((item) => {
      const key = buildDeliveryKey(item);
      if (!key) return;
      const idx = rowIndex.get(key);
      if (idx === undefined) return;
      const deliveredQty = Number(item.delivered_qty) || Number(item.requested_qty) || 0;
      if (deliveredQty <= 0) return;
      const current = rows[idx];
      const nextQty = (Number(current.qty) || 0) + deliveredQty;
      if (nextQty === current.qty) return;

      const mergedPrice = Number(current.price) || Number(item.price) || 0;
      nextRows[idx] = recalcRow({ ...current, qty: nextQty, price: mergedPrice, unit: current.unit || item.unit || "" });
      changed = true;
    });

    if (changed) {
      onChange(nextRows, { forceCommit: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveredList, rows, readOnlyLocked]);

  // Автодобавление позиций из выполненных заявок прямо в расчёт
  useEffect(() => {
    if (readOnlyLocked || !unmatchedDelivered.length) return;
    const additions = unmatchedDelivered.map((item) =>
      recalcRow({
        tmc_id: item.tmc_id ?? null,
        name: item.name ?? "",
        code: item.code ?? "",
        unit: item.unit ?? "",
        price: Number(item.price) || 0,
        qty: Number(item.delivered_qty) || Number(item.requested_qty) || 0,
        total: 0,
        supplier_link: item.supplier_link ?? "",
        for_sale: false,
        sale_price: 0,
        sale_sum: 0,
        sale_qty: 0,
        margin_percent: 0,
        profit: 0,
      })
    );
    onChange([...rows, ...additions], { forceCommit: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unmatchedDelivered.length, readOnlyLocked]);

  const handleCreateTmc = () => {
    if (typeof window === "undefined") return;
    const url = `${window.location.origin}/tmc/new`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const triggerExcelUpload = () => {
    if (readOnlyLocked) return;
    fileInputRef.current?.click();
  };

  const handleExcelImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || typeof onChange !== "function") return;
    setImporting(true);
    try {
      const excelItems = await parseTwoColumnExcel(file);
      const { matched, missing } = matchExcelItems(excelItems, consumablesDict);
      if (!matched.length) {
        alert("Не найдено совпадений в справочнике ТМЦ.");
        return;
      }
      let base = rows;
      if (rows.length > 0) {
        const replace = window.confirm(
          "Очистить текущий список ТМЦ перед импортом? Нажмите Отмена, чтобы добавить строки в конец."
        );
        base = replace ? [] : rows.slice();
      } else {
        base = [];
      }

      const nextRows = base.concat(
        matched.map(({ excel, dict }) =>
          recalcRow({
            tmc_id: dict.id ?? null,
            name: dict.name ?? excel.originalName,
            code: dict.code ?? "",
            unit: dict.unit ?? "",
            price: Number(dict.price) || 0,
            qty: excel.quantity,
            total: 0,
            supplier_link: dict.supplier_link ?? "",
            for_sale: false,
            sale_price: 0,
            sale_sum: 0,
            margin_percent: 0,
            profit: 0,
          })
        )
      );

      onChange(nextRows, { forceCommit: true });
      setDraftRow(createEmptyRow());

      let message = `Импортировано строк: ${matched.length}.`;
      if (missing.length) {
        const preview = missing.slice(0, 10).join(", ");
        message += `\nНе найдены в справочнике (${missing.length}): ${preview}${
          missing.length > 10 ? "…" : ""
        }`;
      }
      alert(message);
    } catch (err) {
      alert(err?.message || "Ошибка импорта Excel");
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteAll = () => {
    if (readOnlyLocked || !rows.length) return;
    const ok = window.confirm("Удалить все позиции ТМЦ на этой странице?");
    if (!ok) return;
    onChange([], { forceCommit: true });
    setDraftRow(createEmptyRow());
  };

  const exportToExcel = () => {
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const title = "Карточка расчёта ТМЦ";
    const generatedAt = `Сформировано: ${new Date().toLocaleString('ru-RU')}`;
    const headers = [
      "№ п/п",
      "Наименование",
      "Код",
      "Кол-во",
      "Ед. изм.",
      "Цена за ед.",
      "Итого, руб. с НДС",
      "По заявкам, кол-во",
      "По заявкам, сумма",
      "Ссылка",
      "ТМЦ на продажу",
      "Продажная цена за ед., руб. с НДС",
      "Сумма продажная, руб. с НДС",
      "Рентабельность продажи ТМЦ, %",
      "Прибыль от продажи ТМЦ, руб",
    ];

    const bodyRows = rows.map((row, idx) => {
      const delivered = deliveredIndex.get(buildDeliveryKey(row)) || { qty: 0, sum: 0 };
      return [
        idx + 1,
        row.name || "",
        row.code || "",
        Number(row.qty) || 0,
        row.unit || "",
        Number(row.price) || 0,
        Number(row.total) || 0,
        delivered.qty || 0,
        delivered.sum || 0,
        row.supplier_link || "",
        row.for_sale ? "Да" : "Нет",
        Number(row.sale_price) || 0,
        Number(row.sale_sum) || 0,
        Number(row.margin_percent) || 0,
        Number(row.profit) || 0,
      ];
    });

    const totalQty = rows.reduce((sum, row) => sum + (Number(row.qty) || 0), 0);
    const totalDeliveredQty = deliveredTotals.qty || 0;
    const totalDeliveredSum = deliveredTotals.sum || 0;
    const totalSaleSum = rows.reduce((sum, row) => sum + (Number(row.sale_sum) || 0), 0);
    const totalProfit = rows.reduce((sum, row) => sum + (Number(row.profit) || 0), 0);
    const totalsRow = [
      "",
      "ИТОГО",
      "",
      totalQty,
      "",
      "",
      totals.totalG || 0,
      totalDeliveredQty,
      totalDeliveredSum,
      "",
      "",
      "",
      totalSaleSum,
      weightedMargin,
      totalProfit,
    ];

    const sheetData = [[title], [generatedAt], [], headers, ...bodyRows, totalsRow];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: headers.length - 1 } },
    ];
    ws["!cols"] = [
      8, 34, 16, 11, 12, 14, 16, 14, 15, 28, 12, 14, 16, 14, 16,
    ].map((wch) => ({ wch }));

    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let row = range.s.r; row <= range.e.r; row += 1) {
      for (let col = range.s.c; col <= range.e.c; col += 1) {
        const cell = ws[XLSX.utils.encode_cell({ r: row, c: col })];
        if (!cell) continue;
        cell.s = cell.s || {};
        cell.s.border = {
          top: { style: "thin", color: { rgb: "D9E2F2" } },
          bottom: { style: "thin", color: { rgb: "D9E2F2" } },
          left: { style: "thin", color: { rgb: "D9E2F2" } },
          right: { style: "thin", color: { rgb: "D9E2F2" } },
        };
        cell.s.alignment = { vertical: "center", horizontal: col === 1 || col === 2 || col === 4 || col === 9 ? "left" : col === 0 ? "center" : "right", wrapText: true };
        if (row === 0) {
          cell.s.font = { bold: true, color: { rgb: "FFFFFF" }, sz: 14 };
          cell.s.fill = { patternType: "solid", fgColor: { rgb: "1D4ED8" } };
          cell.s.alignment = { vertical: "center", horizontal: "center", wrapText: true };
        } else if (row === 1) {
          cell.s.font = { italic: true, color: { rgb: "475569" } };
          cell.s.fill = { patternType: "solid", fgColor: { rgb: "F8FAFC" } };
          cell.s.alignment = { vertical: "center", horizontal: "center", wrapText: true };
        } else if (row === 3) {
          cell.s.font = { bold: true, color: { rgb: "0F172A" } };
          cell.s.fill = { patternType: "solid", fgColor: { rgb: "E2E8F0" } };
          cell.s.alignment = { vertical: "center", horizontal: "center", wrapText: true };
        } else if (row === range.e.r) {
          cell.s.font = { bold: true, color: { rgb: "0F172A" } };
          cell.s.fill = { patternType: "solid", fgColor: { rgb: "DCFCE7" } };
        }

        if (row >= 4 && row < range.e.r) {
          if ([3, 7].includes(col)) cell.z = "0.000";
          if ([5, 6, 8, 11, 12, 14].includes(col)) cell.z = '#,##0.00';
          if (col === 13) cell.z = '0.00';
        }
        if (row === range.e.r) {
          if ([3, 7].includes(col)) cell.z = "0.000";
          if ([5, 6, 8, 11, 12, 14].includes(col)) cell.z = '#,##0.00';
          if (col === 13) cell.z = '0.00';
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ТМЦ");
    XLSX.writeFile(wb, `tmc_card_${stamp}.xlsx`, { cellStyles: true });
  };

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          marginBottom: 12,
          display: "flex",
          justifyContent: "flex-end",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          onClick={handleCreateTmc}
          style={{
            padding: "8px 16px",
            borderRadius: 8,
            border: "1px solid #93c5fd",
            background: "#e0ecff",
            color: "#1d4ed8",
            fontWeight: 700,
            cursor: "pointer",
            whiteSpace: "nowrap",
            opacity: readOnlyLocked ? 0.6 : 1,
            pointerEvents: readOnlyLocked ? "none" : "auto",
          }}
        >
          Создать новую позицию ТМЦ
        </button>

        <button
          type="button"
          onClick={handleDeleteAll}
          disabled={readOnlyLocked || !rows.length}
          style={{
            padding: "8px 16px",
            borderRadius: 8,
            border: "1px solid #ef4444",
            background: "#fee2e2",
            color: "#b91c1c",
            fontWeight: 700,
            cursor: readOnlyLocked || !rows.length ? "not-allowed" : "pointer",
            whiteSpace: "nowrap",
            opacity: readOnlyLocked || !rows.length ? 0.6 : 1,
          }}
        >
          Удалить все
        </button>

        <button
          type="button"
          onClick={exportToExcel}
          disabled={!rows.length}
          style={{
            padding: "8px 16px",
            borderRadius: 8,
            border: "1px solid #16a34a",
            background: "#dcfce7",
            color: "#166534",
            fontWeight: 700,
            cursor: !rows.length ? "not-allowed" : "pointer",
            whiteSpace: "nowrap",
            opacity: !rows.length ? 0.6 : 1,
          }}
        >
          Выгрузить в Excel
        </button>

        <button
          type="button"
          onClick={triggerExcelUpload}
          disabled={readOnlyLocked || importing}
          style={{
            padding: "8px 16px",
            borderRadius: 8,
            border: "1px solid #0ea5e9",
            background: "#e0f2fe",
            color: "#0f172a",
            fontWeight: 700,
            cursor: readOnlyLocked ? "not-allowed" : "pointer",
            flexGrow: 0,
            whiteSpace: "nowrap",
            opacity: readOnlyLocked ? 0.6 : 1,
          }}
        >
          {importing ? "Импорт…" : "Загрузить из Excel"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={handleExcelImport}
          disabled={readOnlyLocked || importing}
        />
      </div>

      <table style={tableStyle}>

        <thead>
          <tr>
            <th style={thGray}>№ п/п</th>
            <th style={thGray}>Наименование</th>
            <th style={thGray}>Код</th>
            <th style={thGray}>Кол-во</th>
            <th style={thGray}>Ед. изм.</th>
            <th style={thGray}>
              <div style={thHeaderStack}>
                <div>цена за ед.</div>
                <button type="button" onClick={refreshPrices} style={headerBtn} title="Подтянуть актуальные цены из справочника">
                  Обновить цены
                </button>
              </div>
            </th>
            <th style={thGray}>Итого, руб. с НДС</th>
            <th style={thGray}>По заявкам, кол-во</th>
            <th style={thGray}>По заявкам, сумма</th>
            <th style={thGray}>Ссылка</th>
            <th style={thGray}>ТМЦ на продажу</th>
            <th style={thGray}>Продажная цена за ед., руб. с НДС</th>
            <th style={thGray}>Сумма продажная, руб. с НДС</th>
            <th style={thGray}>Рентабельность продажи ТМЦ, %</th>
            <th style={thGray}>Прибыль от продажи ТМЦ, руб</th>
          </tr>
        </thead>

        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={15} style={{ ...roGray, textAlign: "center", color: "#999", whiteSpace: "normal" }}>
                Нет позиций. Заполните строку ниже — она сохранится автоматически.
              </td>
            </tr>
          )}

          {renderedRows}
        </tbody>

        {(rows.length > 0 || deliveredTotals.sum > 0) && (
          <tfoot>
            <tr>
              <td style={roGray} />
              <td style={{ ...roGray, fontWeight: 800, whiteSpace: "normal" }}>ИТОГО</td>
              <td style={roGray} />
              <td style={roGray} />
              <td style={roGray} />
              <td style={roGray} />
              <td style={{ ...roGrayRight, fontWeight: 800 }}>{fmtMoney(totals.totalG)}</td>
              <td style={{ ...roGrayRight, fontWeight: 800 }}>{fmtQty(deliveredTotals.qty)}</td>
              <td style={{ ...roGrayRight, fontWeight: 800 }}>{fmtMoney(deliveredTotals.sum)}</td>
              <td style={roGray} />
              <td style={roGray} />
              <td style={roGray} />
              <td style={{ ...roGrayRight, fontWeight: 800 }}>{fmtMoney(totals.totalK)}</td>
              <td style={{ ...roGrayRight, fontWeight: 800 }}>{fmtPercent(weightedMargin)}</td>
              <td style={{ ...roGrayRight, fontWeight: 800 }}>{fmtMoney(totals.totalM)}</td>
            </tr>
          </tfoot>
        )}
      </table>

      {unmatchedDelivered.length > 0 && (
        <div style={{ marginTop: 12, padding: "14px 18px", border: "1px dashed #c7d2fe", borderRadius: 12, background: "#f8fafc", color: "#0f3057" }}>
          Позиции из завершённых заявок автоматически добавлены в расчёт.
        </div>
      )}
    </div>
  );
}

/* Выпадающий выбор "Наименование" — портал поверх всего (расширенная ширина + отступ слева) */
function NamePicker({ value, dict, onPick, onClear, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const anchorRef = useRef(null);
  const dropdownRef = useRef(null);
  const [ddStyle, setDdStyle] = useState({ top: 0, left: 0, width: 0, maxHeight: 420 });

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = Array.isArray(dict) ? dict : [];
    if (!s) return base.slice(0, 300);
    return base
      .filter(
        (d) =>
          String(d.name || "").toLowerCase().includes(s) ||
          String(d.code || "").toLowerCase().includes(s)
      )
      .slice(0, 300);
  }, [dict, q]);

  const positionDropdown = useCallback(() => {
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();

    const margin = 16;
    const leftOffset = 14;
    const vpW = window.innerWidth;
    const vpH = window.innerHeight;
    const minWidth = Math.min(720, vpW - margin * 2);
    const desiredWidth = Math.max(r.width, minWidth);
    const maxLeft = vpW - desiredWidth - margin;
    const baseLeft = r.left + leftOffset;
    const leftPx = Math.max(margin, Math.min(baseLeft, maxLeft));

    const dropdownEl = dropdownRef.current;
    const estimatedHeight = dropdownEl?.offsetHeight || 320;
    const viewportAllowance = Math.max(200, Math.min(420, vpH - margin * 2));
    const maxHeight = viewportAllowance;
    const usedHeight = Math.min(estimatedHeight, viewportAllowance);

    const availableBottom = vpH - r.bottom - margin;
    const availableTop = r.top - margin;
    const shouldOpenUp = availableBottom < usedHeight && availableTop > availableBottom;
    const baseTop = shouldOpenUp ? r.top - usedHeight : r.bottom;
    const clampedTop = Math.max(margin, Math.min(baseTop, vpH - margin - usedHeight));

    setDdStyle({
      top: Math.round(clampedTop),
      left: Math.round(leftPx),
      width: Math.round(desiredWidth),
      maxHeight: Math.round(maxHeight),
    });
  }, []);

  const toggleOpen = () => {
    if (disabled) return;
    setOpen((prev) => {
      const next = !prev;
      if (!next) return next;
      positionDropdown();
      requestAnimationFrame(positionDropdown);
      setTimeout(positionDropdown, 120);
      return next;
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    positionDropdown();
    const raf = requestAnimationFrame(positionDropdown);
    const timeout = setTimeout(positionDropdown, 120);
    const onWin = () => positionDropdown();
    window.addEventListener("scroll", onWin, true);
    window.addEventListener("resize", onWin, true);
    const onDoc = (e) => {
      const a = anchorRef.current;
      const d = dropdownRef.current;
      if ((a && a.contains(e.target)) || (d && d.contains(e.target))) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc, true);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
      window.removeEventListener("scroll", onWin, true);
      window.removeEventListener("resize", onWin, true);
      document.removeEventListener("mousedown", onDoc, true);
    };
  }, [open, positionDropdown]);
  useEffect(() => {
    if (open) positionDropdown();
  }, [open, filtered.length, q, positionDropdown]);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <button
        ref={anchorRef}
        type="button"
        onClick={toggleOpen}
        title={value}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "4px 0",
          border: "none",
          borderBottom: disabled ? "1px solid #dfe3eb" : "1px solid rgba(84,39,176,0.28)",
          borderRadius: 0,
          textAlign: "left",
          background: "transparent",
          cursor: disabled ? "not-allowed" : "text",
          minHeight: 30,
        }}
      >
        <div style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3, color: value ? "#111317" : "#9ca3af", fontSize: "0.95rem" }}>
          {value || "— выберите позицию из справочника —"}
        </div>
      </button>

      {open && !disabled &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              top: ddStyle.top,
              left: ddStyle.left,
              width: ddStyle.width,
              maxHeight: ddStyle.maxHeight || 420,
              overflowY: "auto",
              overflowX: "hidden",
              background: "#fff",
              border: "1px solid #ccc",
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(0,0,0,.2)",
              zIndex: 9999,
              boxSizing: "border-box",
            }}
          >
            <div style={{ padding: 8, borderBottom: "1px solid #eee", display: "flex", gap: 8 }}>
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Поиск по названию или коду…"
                style={{ flex: 1, padding: "6px 8px", border: "1px solid #ccc", borderRadius: 6 }}
              />
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setQ("");
                  onClear?.();
                  setOpen(false);
                }}
                style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 6, background: "#f5f5f5", cursor: "pointer", whiteSpace: "nowrap" }}
                title="Очистить"
              >
                Очистить
              </button>
            </div>

            <div>
              {filtered.length === 0 && <div style={{ padding: 10, color: "#777" }}>Ничего не найдено</div>}
              {filtered.map((d) => (
                <div
                  key={d.id}
                  role="button"
                  tabIndex={0}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onPick(d);
                    setOpen(false);
                  }}
                  style={{ width: "100%", padding: "8px 10px", textAlign: "left", borderBottom: "1px solid #f2f2f2", background: "transparent", cursor: "pointer" }}
                  title={d.name}
                >
                  <div style={{ fontWeight: 700, lineHeight: 1.25, whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    {d.name}
                  </div>
                  <div style={{ fontSize: 12, color: "#666", marginTop: 4, whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    Код: {d.code ?? "—"} • Ед.: {d.unit ?? "—"} • Цена: {fmtMoney(d.price)}
                  </div>
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}